import { useState, useEffect, useCallback } from 'react';
import {
  applyExpandToLayouts,
  buildLayoutItem,
  buildResponsiveLayouts,
  mergeStoredWithConstraints,
} from './layoutBuilders';
import {
  getStorageKey,
  parseLayoutsFromStorage,
  saveLayoutsToStorage,
} from './layoutStorage';

/**
 * Fetches a dashboard layout and builds the responsive grid configuration.
 *
 * @param {Object}   params
 * @param {string}   params.dashboardUuid        - UUID of the dashboard to load.
 * @param {string}   params.userId               - Current user ID (used for localStorage key).
 * @param {Function} params.fetchDashboardLayout  - Data adapter injected from context.
 *   Signature: (dashboardUuid, options?) => Promise<{ data: DashboardLayout }>
 *   `options.signal` carries an AbortSignal for cancellation on unmount.
 */
export const useWidgetGrid = ({ dashboardUuid, userId, fetchDashboardLayout }) => {
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);

  const storageKey = getStorageKey(userId, dashboardUuid);

  useEffect(() => {
    if (!dashboardUuid) {
      setConfigLoading(false);
      setConfigError(new Error('Missing required param: dashboardUuid'));
      return;
    }

    setConfigLoading(true);
    setConfigError(null);

    const abortController = new AbortController();
    let mounted = true;

    fetchDashboardLayout(dashboardUuid, { signal: abortController.signal })
      .then((response) => {
        if (!mounted || abortController.signal.aborted) return;

        const dashboard = response.data;
        const contractWidgets = dashboard.widgets || [];

        const widgetsByUuid = Object.fromEntries(
          contractWidgets.map((w) => [w.instance_uuid, w]),
        );

        const defaultLayout = contractWidgets.map(buildLayoutItem);

        const storedLayouts = parseLayoutsFromStorage(storageKey);
        const rawLgLayout = storedLayouts?.lg || defaultLayout;
        const lgLayout = storedLayouts?.lg
          ? mergeStoredWithConstraints(rawLgLayout, widgetsByUuid)
          : defaultLayout;

        const layouts = buildResponsiveLayouts(lgLayout);

        setWidgetConfig({
          dashboardUuid: dashboard.dashboard_uuid,
          name: dashboard.name,
          version: dashboard.version,
          filters: dashboard.filters || [],
          gridConfig: { cols: 12, rowHeight: 24 },
          widgets: contractWidgets,
          layouts,
        });
      })
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        if (!mounted || abortController.signal.aborted) return;

        console.error('[useWidgetGrid] fetchDashboardLayout error:', err);
        setConfigError(err);
        setWidgetConfig(null);
      })
      .finally(() => {
        if (!mounted || abortController.signal.aborted) return;
        setConfigLoading(false);
      });

    return () => {
      mounted = false;
      abortController.abort();
    };
  }, [dashboardUuid, storageKey, fetchDashboardLayout]);

  const handleLayoutChange = useCallback(
    (newLayout, newLayouts) => {
      if (!newLayouts) return;

      setWidgetConfig((prev) => {
        if (!prev) return prev;
        const widgetsByUuid = Object.fromEntries(
          (prev.widgets || []).map((w) => [w.instance_uuid, w]),
        );
        const merged = {};
        for (const [bp, layout] of Object.entries(newLayouts)) {
          if (layout?.length) {
            merged[bp] = mergeStoredWithConstraints(layout, widgetsByUuid);
          } else {
            merged[bp] = layout;
          }
        }
        const expanded = applyExpandToLayouts(merged);
        saveLayoutsToStorage(storageKey, expanded);
        return { ...prev, layouts: expanded };
      });
    },
    [storageKey],
  );

  return {
    widgetConfig,
    configLoading,
    configError,
    handleLayoutChange,
  };
};
